import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { isEmpty } from 'lodash';
import UploadSupportingDocModal from '../../Pages/DevlopmentOrder/DesignDetail/Components/UploadSupportingDocModal';
import { getDocumentsByLocation } from '../../Pages/AssetLibrary/Assets/store/asset.service';
import { saveDocuments, uploadBomSupportingDocInBackground } from './store/SupportingDocument.action';
import { runInstantQueueTask } from '../../services/background-upload';
import { customStyles, colourWayOptions } from '../helper';

const BOM_SUPPORTING_DOC_MODULE = 'bom-supporting-doc';

/** File types a BOM supporting document may be uploaded as — drives both the dropzone and the Asset Library picker. */
const BOM_ACCEPTED_EXTENSIONS =
  '.pdf, .ai, .eps, .psd, .dxf, .rul, .wds, .doc, .docx, .xls, .xlsx, .csv, .txt, .png, .jpg, .jpeg, .gif, .webp, .bmp, .svg, .tif, .tiff';

/**
 * BOM supporting-document upload. Renders the shared asset-style upload modal with the BOM-only
 * fields (document type, colourway, reference, placement) below the uploader. A newly dropped file
 * uploads through the resumable, no-size-limit background multipart flow; picking an existing Asset
 * Library file saves directly via the legacy multipart endpoint the asset picker expects.
 */
function AddSupportingDocuments({ show, handlePopup, refreshDocs = () => {}, data, referenceOptions }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const reducerData = useSelector((reducerState) => reducerState);
  const { ColorLibraryDetails, SystemMastersDetails, BOMConfigDetails } = reducerData;
  const { ColorLibList } = ColorLibraryDetails;
  const { placement_master } = SystemMastersDetails;
  const { productDetails } = BOMConfigDetails || {};

  const [radioSelected, setRadioSelected] = useState(false);
  const [showColourWayError, setShowColourWayError] = useState(false);
  const [qualitySelected, setQualitySelected] = useState(true);
  const [colourWays, setColourWays] = useState(null);
  const [reference, setReference] = useState(null);
  const [placement, setPlacement] = useState(null);

  /**
   * Lists Asset Library files for the picker — scoped to the development order when the BOM is
   * opened from one, unscoped for library BOMs, which have no product context.
   */
  const fetchDocuments = useCallback(
    (search, page, limit) => getDocumentsByLocation(data?.devOrderId || '', search, BOM_ACCEPTED_EXTENSIONS, undefined, page, limit),
    [data?.devOrderId],
  );

  /**
   * Builds the BOM background-upload context from the modal's current selection.
   * @param {Object} uploadedDoc - The picker's selected document descriptor.
   * @returns {Object} Context passed to the background upload action.
   */
  const buildUploadContext = (uploadedDoc) => ({
    fileName: `${uploadedDoc.fileName}.${uploadedDoc.fileExtn}`,
    bomType: data?.bomType,
    libId: data?.libId || null,
    devOrderId: data?.devOrderId || null,
    bomId: data?.bomId || null,
    docType: radioSelected ? 'Colour' : 'Quality',
    colourWay: radioSelected ? colourWays?._id : null,
    placement: placement?._id || null,
    reference: JSON.stringify(reference) || null,
    productNumber: productDetails?.number || null,
    isAddToAssetLibrary: uploadedDoc.addToAssetLibrary === true,
  });

  /**
   * Appends the existing-asset save fields the legacy `/bom-supporting/upload` endpoint expects.
   * @param {FormData} formData - Target multipart body.
   * @param {Object} uploadedDoc - The picker's selected document descriptor.
   */
  const appendExistingAssetForm = (formData, uploadedDoc) => {
    formData.append('bomId', data?.bomId ? data?.bomId : null);
    if (data?.devOrderId) {
      formData.append('devOrderId', data?.devOrderId ? data?.devOrderId : null);
    }
    formData.append('fileName', `${uploadedDoc.fileName}.${uploadedDoc.fileExtn}`);
    formData.append('libId', data?.libId ? data?.libId : null);
    formData.append('bomType', data?.bomType);
    if (uploadedDoc?.document) {
      formData.append('document', uploadedDoc.document);
      formData.append('isAddToAssetLibrary', uploadedDoc.addToAssetLibrary === true);
    } else if (uploadedDoc?.existingAsset) {
      formData.append('existingAssetId', uploadedDoc.existingAsset.id);
    }
    formData.append('docType', radioSelected ? 'Colour' : 'Quality');
    formData.append('colourWay', radioSelected ? colourWays?._id : null);
    formData.append('placement', placement?._id || null);
    formData.append('reference', JSON.stringify(reference) || null);
    formData.append('documentType', 'document');
  };

  /**
   * Validates the BOM-specific fields and passes the raw selection through to `submitData`, which
   * chooses the upload path. Returns null when a Colour document still has no colourway so the
   * modal stays open with the error shown.
   */
  const buildPayload = (uploadedDoc) => {
    if (radioSelected && isEmpty(colourWays?._id)) {
      setShowColourWayError(true);
      return null;
    }
    return { uploadedDoc };
  };

  /**
   * Routes the selection: a newly dropped file goes through the resumable, no-limit background
   * multipart flow; an existing Asset Library pick saves directly via the legacy endpoint. Closes
   * the modal when the header close button reports 'close'.
   */
  const submitData = async (payload) => {
    if (payload === 'close') {
      handlePopup();
      return;
    }
    const { uploadedDoc } = payload;
    if (uploadedDoc?.document) {
      const context = buildUploadContext(uploadedDoc);
      const onComplete = () => {
        if (typeof refreshDocs === 'function') {
          refreshDocs();
        }
      };
      dispatch(uploadBomSupportingDocInBackground(context, uploadedDoc.document, t, onComplete));
      handlePopup();
      return;
    }
    if (uploadedDoc?.existingAsset) {
      const formData = new FormData();
      appendExistingAssetForm(formData, uploadedDoc);
      handlePopup();
      dispatch(
        runInstantQueueTask(dispatch, {
          fileName: `${uploadedDoc.fileName}.${uploadedDoc.fileExtn}`,
          module: BOM_SUPPORTING_DOC_MODULE,
          devOrderId: data?.devOrderId,
          persist: async () => Boolean(await dispatch(saveDocuments(formData, t))),
          onSuccess: () => {
            if (typeof refreshDocs === 'function') {
              refreshDocs();
            }
          },
        }),
      );
    }
  };

  const handleRadioButton = async (e) => {
    if (e.target.id === 'status_Colour') {
      setRadioSelected(e.target.checked);
      setQualitySelected(!e.target.checked);
    } else {
      setRadioSelected(!e.target.checked);
      setQualitySelected(e.target.checked);
    }
  };

  const handleColourWays = (ele) => {
    setColourWays(ele);
    setShowColourWayError(false);
  };

  const handleReference = (ele) => {
    setReference(ele);
  };

  const handlePlacement = (ele) => {
    setPlacement(ele);
  };

  const validReferenceOptions = useMemo(() => {
    if (referenceOptions?.length) {
      return referenceOptions.some((option) => !option?.name) ? [] : referenceOptions;
    }
    return [];
  }, [referenceOptions]);

  return (
    <UploadSupportingDocModal
      showUpload={show}
      module="bomDoc"
      title={t('ADD_SUPPORTING_DOCUMENT.UPLOAD_DOCUMENTS')}
      acceptedFileExtenstion={BOM_ACCEPTED_EXTENSIONS}
      className="asset-style-modal-dialog upload-doc-modal"
      devOrderId={data?.devOrderId}
      fetchDocuments={fetchDocuments}
      buildPayload={buildPayload}
      submitData={submitData}
      modalLoading={false}
    >
      <Row className="mt-16">
        <Col lg={3}>
          <div className="text-bold support-doc-title">{t('ADD_SUPPORTING_DOCUMENT.SUPPORT_DOC_TYPE')}</div>
          <div className="d-flex mt-2">
            <div className="custom-radio d-flex ">
              <input type="radio" id="status_Quality" value={qualitySelected} onChange={handleRadioButton} checked={qualitySelected} />
              <label className="mb-0" htmlFor="status_Quality" />
            </div>
            {t('ADD_SUPPORTING_DOCUMENT.QUALITY')}
            <div className="custom-radio d-flex ml-3">
              <input type="radio" id="status_Colour" value={radioSelected} onChange={handleRadioButton} checked={radioSelected} />
              <label className="mb-0" htmlFor="status_Colour" />
            </div>
            {t('ADD_SUPPORTING_DOCUMENT.COLOUR')}
          </div>
        </Col>
        {radioSelected && (
          <Col lg={3}>
            <div className="asterisk-req text-bold support-doc-title">
              {t('ADD_SUPPORTING_DOCUMENT.COLOURWAY')} <span>*</span>
            </div>
            <div className="common-react-select">
              <Select
                name="colourWays"
                options={colourWayOptions([...ColorLibList])}
                getOptionLabel={(option) => `${option.refName}`}
                getOptionValue={(option) => `${option._id}`}
                value={colourWays}
                styles={customStyles}
                onChange={(e) => handleColourWays(e)}
                placeholder={t('ADD_SUPPORTING_DOCUMENT.COLOURWAY')}
                isClearable
                className="common-select"
                classNamePrefix="common-select"
              />
              {showColourWayError && <span className="error-message">{t('ADD_SUPPORTING_DOCUMENT.COLOURWAY_REQUIRED')}</span>}
            </div>
          </Col>
        )}

        <Col lg={5} className="d-flex">
          <div className="w-100 common-react-select ">
            <p className="mb-0 text-bold asterisk-req support-doc-title">{t('ADD_SUPPORTING_DOCUMENT.REFERENCE')}</p>
            <Select
              name={t('ADD_SUPPORTING_FILE.REFERENCE')}
              options={validReferenceOptions}
              getOptionLabel={(option) => `${option.name}`}
              getOptionValue={(option) => `${option._id}`}
              value={reference}
              styles={customStyles}
              onChange={(e) => handleReference(e)}
              placeholder={t('ADD_SUPPORTING_DOCUMENT.REFERENCE')}
              isClearable
              className="common-select"
              classNamePrefix="common-select"
            />
          </div>
          <div className="pl-2 w-100 common-react-select">
            <p className="mb-0 text-bold asterisk-req support-doc-title">{t('ADD_SUPPORTING_DOCUMENT.PLACEMENT')}</p>

            <Select
              name={t('ADD_SUPPORTING_FILE.PLACEMENT')}
              options={placement_master}
              getOptionLabel={(option) => `${option.refName}`}
              getOptionValue={(option) => `${option._id}`}
              value={placement}
              styles={customStyles}
              onChange={(e) => handlePlacement(e)}
              placeholder={t('ADD_SUPPORTING_DOCUMENT.PLACEMENT')}
              isClearable
              className="common-select"
              classNamePrefix="common-select"
            />
          </div>
        </Col>
      </Row>
    </UploadSupportingDocModal>
  );
}

export default AddSupportingDocuments;
